/* ==========================================================
   HC Brands Directory - hc-brands-directory.js

   Brands render server-side in Liquid via shop.vendors; this file only
   handles search filtering, the alphabet shortcuts, the featured carousel
   arrows, and scroll-to-top.

   The source section shipped this logic twice - an external file plus an
   inlined copy that existed to work around a Broadcast-specific loading
   issue. Summit loads assets normally, so this is the single copy, kept to
   the newer variant (.bd-hidden class + guarded innerHTML).
   ========================================================== */
(function () {
  'use strict';

  var ns = (window.smbHco = window.smbHco || {});

  // Horizon scrolls .page-wrapper, not the window - html and body are
  // overflow:hidden - so window.scroll() from the Broadcast source was a
  // silent no-op here. Find the scroller that actually contains the list.
  function getScroller() {
    var el = document.getElementById('sbp-hco');
    var p = el && el.parentElement;
    while (p) {
      var cs = getComputedStyle(p);
      if (/(auto|scroll)/.test(cs.overflowY) && p.scrollHeight > p.clientHeight) return p;
      p = p.parentElement;
    }
    return document.scrollingElement || document.documentElement;
  }

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  // Hand-rolled scroll animation: rAF, ease-out, ~320ms. Jumps instantly for
  // reduced-motion users AND whenever the document is hidden - background tabs
  // throttle requestAnimationFrame to zero, which stalls any frame-driven
  // scroll (native smooth scrolling included) and was mistaken for a Horizon
  // bug during automated testing. The instant path keeps the outcome correct
  // no matter who or what is driving the page.
  function animateScroll(el, prop, target) {
    var from = el[prop];
    var delta = target - from;
    if (!delta) return;
    if (prefersReducedMotion() || document.hidden) {
      el[prop] = target;
      return;
    }
    var start = null;
    var DURATION = 320;
    function step(ts) {
      if (start === null) start = ts;
      var t = Math.min((ts - start) / DURATION, 1);
      var eased = 1 - Math.pow(1 - t, 3);
      el[prop] = from + delta * eased;
      if (t < 1) requestAnimationFrame(step);
    }
    requestAnimationFrame(step);
  }

  /* ── Helpers referenced by inline handlers in the Liquid ── */

  ns.shortcutClick = function (el) {
    var t = document.getElementById('term-' + el.getAttribute('data-to-term'));
    if (!t) return;
    var instant = prefersReducedMotion() || document.hidden;
    t.scrollIntoView({ behavior: instant ? 'auto' : 'smooth', block: 'start' });
  };

  ns.cancelSearch = function (sid) {
    var el = document.getElementById('brand-search-' + sid);
    if (el) {
      el.value = '';
      el.dispatchEvent(new Event('input'));
    }
  };

  ns.onCarouselScroll = function () {
    var el = document.getElementById('hco-brands-carousel');
    if (!el) return;
    var navRight = document.getElementById('hco-carousel-nav-right');
    var navLeft = document.getElementById('hco-carousel-nav-left');
    var atEnd = Math.abs(el.scrollWidth - el.scrollLeft - el.clientWidth) < 1;
    var atStart = el.scrollLeft < 1;
    if (navRight) {
      navRight.className = atEnd ? '' : 'active';
      navRight.setAttribute('aria-disabled', atEnd ? 'true' : 'false');
    }
    if (navLeft) {
      navLeft.className = atStart ? '' : 'active';
      navLeft.setAttribute('aria-disabled', atStart ? 'true' : 'false');
    }
  };

  // Scroll advance is driven by data-scroll-count on the carousel, which comes
  // from the "Items per arrow click" setting.
  ns.scrollCarousel = function (el, dir) {
    var c = document.getElementById('hco-brands-carousel');
    if (!c) return;
    var itemsToScroll = parseInt(c.dataset.scrollCount, 10) || 3;
    var amount = 172 * itemsToScroll;
    var items = c.getElementsByClassName('carousel-link');
    if (items.length && items[0].offsetWidth > 0) {
      amount = (items[0].offsetWidth + 12) * itemsToScroll;
    }
    animateScroll(c, 'scrollLeft', c.scrollLeft + (dir === 'right' ? amount : -amount));
    setTimeout(ns.onCarouselScroll, 400);
  };

  // Announce the filtered result count to screen readers via the live region.
  // Search hides links with the .bd-hidden class, so count what lacks it.
  ns.announceSearchResults = function (sid) {
    var wrapper = document.getElementById('sbp-hco-brands-wrapper');
    var statusEl = document.getElementById('bd-search-status-' + sid);
    if (!wrapper || !statusEl) return;
    var visible = 0;
    wrapper.querySelectorAll('.brands a').forEach(function (a) {
      if (!a.classList.contains('bd-hidden')) visible++;
    });
    statusEl.textContent = visible + ' brand' + (visible !== 1 ? 's' : '') + ' found';
  };

  /* ── Search ── */

  function escHtml(s) {
    return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  function highlight(str, query) {
    var parts = str.toLowerCase().split(query.toLowerCase());
    if (parts.length === 1) return escHtml(str);
    var result = '';
    var pos = 0;
    for (var i = 0; i < parts.length; i++) {
      result += escHtml(str.slice(pos, pos + parts[i].length));
      pos += parts[i].length;
      if (i < parts.length - 1) {
        result += '<b>' + escHtml(str.slice(pos, pos + query.length)) + '</b>';
        pos += query.length;
      }
    }
    return result;
  }

  function bindSearch() {
    var searchEl = document.querySelector('input[id^="brand-search-"]');
    if (!searchEl || searchEl.dataset.hcbdBound) return;
    searchEl.dataset.hcbdBound = '1';

    var wrapper = document.getElementById('search-wrapper');
    var sid = searchEl.id.replace('brand-search-', '');

    searchEl.addEventListener('input', function () {
      var query = searchEl.value.toLowerCase().trim();
      if (wrapper) {
        query ? wrapper.setAttribute('active', 'true') : wrapper.removeAttribute('active');
      }

      var total = 0;
      document.querySelectorAll('#sbp-hco-brands-wrapper .brand-section').forEach(function (section) {
        var count = 0;
        section.querySelectorAll('.brands a').forEach(function (link) {
          var brand = link.querySelector('.brand');
          var text = link.getAttribute('aria-label') || (brand ? brand.textContent : '') || '';
          var show = !query || text.toLowerCase().includes(query);
          // .bd-hidden carries !important in the CSS, so outside styles can't
          // resurrect a filtered-out brand. innerHTML is guarded so one odd
          // vendor name can't abort the loop and strand the section states.
          link.classList.toggle('bd-hidden', !show);
          if (brand) {
            try {
              brand.innerHTML = show && query ? highlight(text, query) : escHtml(text);
            } catch (e) {
              /* leave the brand text untouched */
            }
          }
          if (show) count++;
        });
        section.classList.toggle('bd-hidden', count === 0);
        total += count;

        var shortcut = document.getElementById('shortcut-' + section.getAttribute('data-term'));
        if (shortcut) {
          count ? shortcut.removeAttribute('disabled') : shortcut.setAttribute('disabled', 'true');
          shortcut.style.color = count ? '' : '#ccc';
        }
      });

      var nr = document.getElementById('no-results');
      if (nr) nr.classList.toggle('bd-hidden', total > 0);

      ns.announceSearchResults(sid);
    });
  }

  /* ── Alphabet shortcuts: disable letters with no brands ── */

  function updateShortcuts() {
    var active = {};
    document.querySelectorAll('#sbp-hco-brands-wrapper .brand-section').forEach(function (s) {
      active[s.getAttribute('data-term')] = true;
    });
    document.querySelectorAll('.shortcut-letter').forEach(function (el) {
      var has = active[el.getAttribute('data-to-term')];
      has ? el.removeAttribute('disabled') : el.setAttribute('disabled', 'true');
      el.style.color = has ? '' : '#ccc';
    });
  }

  /* ── Scroll to top ── */

  function bindScrollToTop() {
    if (document.getElementById('scroll-to-top')) return;
    var btn = document.createElement('button');
    btn.id = 'scroll-to-top';
    btn.type = 'button';
    btn.setAttribute('aria-label', 'Scroll to top of page');
    btn.innerHTML =
      '<svg aria-hidden="true" focusable="false" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M16 15a1 1 0 0 1-.707-.293L12 11.414l-3.293 3.293a1 1 0 1 1-1.414-1.414l4-4a1 1 0 0 1 1.414 0l4 4A1 1 0 0 1 16 15z" style="fill:#757575"/></svg>';
    btn.onclick = function () {
      animateScroll(getScroller(), 'scrollTop', 0);
    };
    document.body.appendChild(btn);
    // Scroll events do not bubble, and the scroller is a container here, so
    // listen in the capture phase at the document to see every scroll.
    document.addEventListener(
      'scroll',
      function () {
        btn.style.display = getScroller().scrollTop > 250 ? 'block' : 'none';
      },
      true
    );
  }

  /* ── Boot ── */

  function init() {
    bindSearch();
    bindScrollToTop();
    updateShortcuts();
    ns.onCarouselScroll();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  window.addEventListener('load', ns.onCarouselScroll);

  // Theme editor: re-rendered sections arrive with fresh, unbound nodes.
  document.addEventListener('shopify:section:load', init);
})();
